import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { scrollableTableWrapStyles, scrollableTableSizing } from '../styles/scrollableTable';
import { FiGlobe, FiPlus, FiEdit, FiTrash2, FiX, FiCheck, FiRefreshCw, FiSearch } from 'react-icons/fi';
import { supabase } from '../../config/supabase';
import { useLanguage } from '../../context/LanguageContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${props => props.variant === 'primary' ? '#5efba9' : 'white'};
  color: ${props => props.variant === 'primary' ? '#1f2937' : '#6b7280'};
  border: ${props => props.variant === 'secondary' ? '1px solid #e5e7eb' : 'none'};

  &:hover {
    background: ${props => props.variant === 'primary' ? '#4de89e' : '#f9fafb'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TableWrapper = styled.div`
  ${scrollableTableWrapStyles}
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Table = styled.table`
  ${scrollableTableSizing}
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: #f9fafb;
`;

const TableHeaderRow = styled.tr`
  border-bottom: 1px solid #e5e7eb;
`;

const TableHeaderCell = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.2s;

  &:hover {
    background: #f9fafb;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #1f2937;
`;

const ActionButtonsCell = styled.div`
  display: flex;
  gap: 8px;
`;

const DefaultBadge = styled.span`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.isDefault ? '#dcfce7' : '#f3f4f6'};
  color: ${props => props.isDefault ? '#166534' : '#6b7280'};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
`;

const EmptyText = styled.p`
  font-size: 16px;
  color: #9ca3af;
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 32px;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  border-radius: 6px;

  &:hover {
    background: #f3f4f6;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  padding: 10px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #5efba9;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #374151;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #e5e7eb;
  background: white;
  color: #6b7280;

  &:hover {
    background: #f9fafb;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 400px;
  margin-bottom: 16px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 44px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const SearchIcon = styled(FiSearch)`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 18px;
  pointer-events: none;
`;

const ClearButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
    color: #6b7280;
  }
`;

const GCC_COUNTRIES = [
  { code: 'OM', name: 'Oman' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'QA', name: 'Qatar' },
  { code: 'BH', name: 'Bahrain' }
];

// All countries except Israel
const ALL_COUNTRIES = [
  { code: 'AF', name: 'Afghanistan' },
  { code: 'AL', name: 'Albania' },
  { code: 'DZ', name: 'Algeria' },
  { code: 'AD', name: 'Andorra' },
  { code: 'AO', name: 'Angola' },
  { code: 'AG', name: 'Antigua and Barbuda' },
  { code: 'AR', name: 'Argentina' },
  { code: 'AM', name: 'Armenia' },
  { code: 'AU', name: 'Australia' },
  { code: 'AT', name: 'Austria' },
  { code: 'AZ', name: 'Azerbaijan' },
  { code: 'BS', name: 'Bahamas' },
  { code: 'BH', name: 'Bahrain' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'BB', name: 'Barbados' },
  { code: 'BY', name: 'Belarus' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BZ', name: 'Belize' },
  { code: 'BJ', name: 'Benin' },
  { code: 'BT', name: 'Bhutan' },
  { code: 'BO', name: 'Bolivia' },
  { code: 'BA', name: 'Bosnia and Herzegovina' },
  { code: 'BW', name: 'Botswana' },
  { code: 'BR', name: 'Brazil' },
  { code: 'BN', name: 'Brunei' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'BF', name: 'Burkina Faso' },
  { code: 'BI', name: 'Burundi' },
  { code: 'KH', name: 'Cambodia' },
  { code: 'CM', name: 'Cameroon' },
  { code: 'CA', name: 'Canada' },
  { code: 'CV', name: 'Cape Verde' },
  { code: 'CF', name: 'Central African Republic' },
  { code: 'TD', name: 'Chad' },
  { code: 'CL', name: 'Chile' },
  { code: 'CN', name: 'China' },
  { code: 'CO', name: 'Colombia' },
  { code: 'KM', name: 'Comoros' },
  { code: 'CG', name: 'Congo' },
  { code: 'CD', name: 'Congo (DRC)' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'HR', name: 'Croatia' },
  { code: 'CU', name: 'Cuba' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'DK', name: 'Denmark' },
  { code: 'DJ', name: 'Djibouti' },
  { code: 'DM', name: 'Dominica' },
  { code: 'DO', name: 'Dominican Republic' },
  { code: 'EC', name: 'Ecuador' },
  { code: 'EG', name: 'Egypt' },
  { code: 'SV', name: 'El Salvador' },
  { code: 'GQ', name: 'Equatorial Guinea' },
  { code: 'ER', name: 'Eritrea' },
  { code: 'EE', name: 'Estonia' },
  { code: 'ET', name: 'Ethiopia' },
  { code: 'FJ', name: 'Fiji' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'GA', name: 'Gabon' },
  { code: 'GM', name: 'Gambia' },
  { code: 'GE', name: 'Georgia' },
  { code: 'DE', name: 'Germany' },
  { code: 'GH', name: 'Ghana' },
  { code: 'GR', name: 'Greece' },
  { code: 'GD', name: 'Grenada' },
  { code: 'GT', name: 'Guatemala' },
  { code: 'GN', name: 'Guinea' },
  { code: 'GW', name: 'Guinea-Bissau' },
  { code: 'GY', name: 'Guyana' },
  { code: 'HT', name: 'Haiti' },
  { code: 'HN', name: 'Honduras' },
  { code: 'HU', name: 'Hungary' },
  { code: 'IS', name: 'Iceland' },
  { code: 'IN', name: 'India' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'IR', name: 'Iran' },
  { code: 'IQ', name: 'Iraq' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IT', name: 'Italy' },
  { code: 'CI', name: 'Ivory Coast' },
  { code: 'JM', name: 'Jamaica' },
  { code: 'JP', name: 'Japan' },
  { code: 'JO', name: 'Jordan' },
  { code: 'KZ', name: 'Kazakhstan' },
  { code: 'KE', name: 'Kenya' },
  { code: 'KI', name: 'Kiribati' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'KG', name: 'Kyrgyzstan' },
  { code: 'LA', name: 'Laos' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LB', name: 'Lebanon' },
  { code: 'LS', name: 'Lesotho' },
  { code: 'LR', name: 'Liberia' },
  { code: 'LY', name: 'Libya' },
  { code: 'LI', name: 'Liechtenstein' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MK', name: 'North Macedonia' },
  { code: 'MG', name: 'Madagascar' },
  { code: 'MW', name: 'Malawi' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'MV', name: 'Maldives' },
  { code: 'ML', name: 'Mali' },
  { code: 'MT', name: 'Malta' },
  { code: 'MH', name: 'Marshall Islands' },
  { code: 'MR', name: 'Mauritania' },
  { code: 'MU', name: 'Mauritius' },
  { code: 'MX', name: 'Mexico' },
  { code: 'FM', name: 'Micronesia' },
  { code: 'MD', name: 'Moldova' },
  { code: 'MC', name: 'Monaco' },
  { code: 'MN', name: 'Mongolia' },
  { code: 'ME', name: 'Montenegro' },
  { code: 'MA', name: 'Morocco' },
  { code: 'MZ', name: 'Mozambique' },
  { code: 'MM', name: 'Myanmar' },
  { code: 'NA', name: 'Namibia' },
  { code: 'NR', name: 'Nauru' },
  { code: 'NP', name: 'Nepal' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'NI', name: 'Nicaragua' },
  { code: 'NE', name: 'Niger' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'KP', name: 'North Korea' },
  { code: 'NO', name: 'Norway' },
  { code: 'OM', name: 'Oman' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'PW', name: 'Palau' },
  { code: 'PS', name: 'Palestine' },
  { code: 'PA', name: 'Panama' },
  { code: 'PG', name: 'Papua New Guinea' },
  { code: 'PY', name: 'Paraguay' },
  { code: 'PE', name: 'Peru' },
  { code: 'PH', name: 'Philippines' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'QA', name: 'Qatar' },
  { code: 'RO', name: 'Romania' },
  { code: 'RU', name: 'Russia' },
  { code: 'RW', name: 'Rwanda' },
  { code: 'KN', name: 'Saint Kitts and Nevis' },
  { code: 'LC', name: 'Saint Lucia' },
  { code: 'VC', name: 'Saint Vincent and the Grenadines' },
  { code: 'WS', name: 'Samoa' },
  { code: 'SM', name: 'San Marino' },
  { code: 'ST', name: 'Sao Tome and Principe' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'SN', name: 'Senegal' },
  { code: 'RS', name: 'Serbia' },
  { code: 'SC', name: 'Seychelles' },
  { code: 'SL', name: 'Sierra Leone' },
  { code: 'SG', name: 'Singapore' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'SB', name: 'Solomon Islands' },
  { code: 'SO', name: 'Somalia' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'KR', name: 'South Korea' },
  { code: 'SS', name: 'South Sudan' },
  { code: 'ES', name: 'Spain' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'SD', name: 'Sudan' },
  { code: 'SR', name: 'Suriname' },
  { code: 'SZ', name: 'Eswatini' },
  { code: 'SE', name: 'Sweden' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'SY', name: 'Syria' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'TJ', name: 'Tajikistan' },
  { code: 'TZ', name: 'Tanzania' },
  { code: 'TH', name: 'Thailand' },
  { code: 'TL', name: 'Timor-Leste' },
  { code: 'TG', name: 'Togo' },
  { code: 'TO', name: 'Tonga' },
  { code: 'TT', name: 'Trinidad and Tobago' },
  { code: 'TN', name: 'Tunisia' },
  { code: 'TR', name: 'Turkey' },
  { code: 'TM', name: 'Turkmenistan' },
  { code: 'TV', name: 'Tuvalu' },
  { code: 'UG', name: 'Uganda' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'US', name: 'United States' },
  { code: 'UY', name: 'Uruguay' },
  { code: 'UZ', name: 'Uzbekistan' },
  { code: 'VU', name: 'Vanuatu' },
  { code: 'VA', name: 'Vatican City' },
  { code: 'VE', name: 'Venezuela' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'YE', name: 'Yemen' },
  { code: 'ZM', name: 'Zambia' },
  { code: 'ZW', name: 'Zimbabwe' }
];

export default function SuperAdminShippingCountries() {
  const { isArabic } = useLanguage();
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCountry, setEditingCountry] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    is_default: false
  });

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('shipping_countries')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;
      setCountries(data || []);
    } catch (err) {
      console.error('Error fetching countries:', err);
      alert('Failed to fetch countries: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (country) => {
    setEditingCountry(country);
    setFormData({
      code: country.code || '',
      name: country.name || '',
      is_default: country.is_default || false
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (countryId) => {
    if (!window.confirm('Are you sure you want to delete this country?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('shipping_countries')
        .delete()
        .eq('id', countryId);

      if (error) throw error;
      
      alert('Country deleted successfully!');
      fetchCountries();
    } catch (err) {
      console.error('Error deleting country:', err);
      alert('Failed to delete country: ' + err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const countryData = {
        code: formData.code.trim().toUpperCase(),
        name: formData.name.trim(),
        is_default: formData.is_default
      };

      let error;
      if (editingCountry) {
        const { error: updateError } = await supabase
          .from('shipping_countries')
          .update(countryData)
          .eq('id', editingCountry.id);
        error = updateError;
      } else {
        const { error: insertError } = await supabase
          .from('shipping_countries')
          .insert([countryData]);
        error = insertError;
      }

      if (error) {
        console.error('Error details:', error);
        
        // Check if it's an RLS error
        if (error.code === 'PGRST116' || error.message?.includes('row-level security')) {
          throw new Error('RLS Policy Error: You need to update the database RLS policy. See console for SQL fix.');
        }
        
        throw error;
      }
      
      alert(editingCountry ? 'Country updated successfully!' : 'Country added successfully!');
      setIsModalOpen(false);
      setEditingCountry(null);
      setFormData({
        code: '',
        name: '',
        is_default: false
      });
      fetchCountries();
    } catch (err) {
      console.error('Error saving country:', err);
      alert('Failed to save country: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCountry(null);
    setFormData({
      code: '',
      name: '',
      is_default: false
    });
  };

  const handleSelectCountry = (e) => {
    const selectedCode = e.target.value;
    if (!selectedCode) return;

    const selectedCountry = ALL_COUNTRIES.find(c => c.code === selectedCode);
    if (selectedCountry) {
      setFormData({
        code: selectedCountry.code,
        name: selectedCountry.name,
        is_default: false
      });
    }
  };

  const toggleDefault = async (countryId) => {
    try {
      // Set this country as default
      const { error } = await supabase
        .from('shipping_countries')
        .update({ is_default: true })
        .eq('id', countryId);

      if (error) throw error;
      
      fetchCountries();
    } catch (err) {
      console.error('Error setting default country:', err);
      alert('Failed to set default country: ' + err.message);
    }
  };

  const handleQuickAddAllGCC = async () => {
    if (!window.confirm('Add all 6 GCC countries? (Oman, UAE, Saudi Arabia, Kuwait, Qatar, Bahrain)')) {
      return;
    }

    try {
      setLoading(true);
      
      // Check which countries already exist
      const existingCodes = countries.map(c => c.code);
      const newCountries = GCC_COUNTRIES.filter(gcc => !existingCodes.includes(gcc.code));

      if (newCountries.length === 0) {
        alert('All GCC countries are already added!');
        setLoading(false);
        return;
      }

      // Insert new countries (first one as default if no default exists)
      const hasDefault = countries.some(c => c.is_default);
      const countriesToInsert = newCountries.map((country, index) => ({
        code: country.code,
        name: country.name,
        is_default: !hasDefault && index === 0 // Set first as default if no default exists
      }));

      const { error } = await supabase
        .from('shipping_countries')
        .insert(countriesToInsert);

      if (error) throw error;
      
      alert(`Successfully added ${newCountries.length} GCC country/countries!`);
      fetchCountries();
    } catch (err) {
      console.error('Error adding GCC countries:', err);
      alert('Failed to add GCC countries: ' + err.message);
      setLoading(false);
    }
  };

  const handleQuickAddAllCountries = async () => {
    if (!window.confirm(`Add all ${ALL_COUNTRIES.length} countries (excluding Israel)? This may take a moment.`)) {
      return;
    }

    try {
      setLoading(true);
      
      // Check which countries already exist
      const existingCodes = countries.map(c => c.code);
      const newCountries = ALL_COUNTRIES.filter(country => !existingCodes.includes(country.code));

      if (newCountries.length === 0) {
        alert('All countries are already added!');
        setLoading(false);
        return;
      }

      // Insert new countries (first one as default if no default exists)
      const hasDefault = countries.some(c => c.is_default);
      const countriesToInsert = newCountries.map((country, index) => ({
        code: country.code,
        name: country.name,
        is_default: !hasDefault && index === 0 // Set first as default if no default exists
      }));

      const { error } = await supabase
        .from('shipping_countries')
        .insert(countriesToInsert);

      if (error) throw error;
      
      alert(`Successfully added ${newCountries.length} countries!`);
      fetchCountries();
    } catch (err) {
      console.error('Error adding all countries:', err);
      alert('Failed to add all countries: ' + err.message);
      setLoading(false);
    }
  };

  // Filter countries by search term
  const filteredCountries = useMemo(() => {
    if (!searchTerm.trim()) {
      return countries;
    }
    const term = searchTerm.toLowerCase();
    return countries.filter(country => {
      const code = (country.code || '').toLowerCase();
      const name = (country.name || '').toLowerCase();
      const isDefault = country.is_default ? 'default' : '';
      return code.includes(term) || name.includes(term) || isDefault.includes(term);
    });
  }, [countries, searchTerm]);

  return (
    <>
      <Container>
        <Header>
          <Title>
            <FiGlobe /> {isArabic ? 'دول الشحن' : 'Shipping Countries'}
          </Title>
          <HeaderActions>
            <Button variant="secondary" onClick={fetchCountries}>
              <FiRefreshCw /> {isArabic ? 'تحديث' : 'Refresh'}
            </Button>
            <Button variant="secondary" onClick={handleQuickAddAllGCC}>
              <FiGlobe /> {isArabic ? 'إضافة دول الخليج سريعًا' : 'Quick Add GCC'}
            </Button>
            <Button variant="secondary" onClick={handleQuickAddAllCountries}>
              <FiGlobe /> {isArabic ? 'إضافة كل الدول سريعًا' : 'Quick Add All Countries'}
            </Button>
            <Button variant="primary" onClick={() => setIsModalOpen(true)}>
              <FiPlus /> {isArabic ? 'إضافة دولة' : 'Add Country'}
            </Button>
          </HeaderActions>
        </Header>

        <SearchContainer>
          <SearchIcon />
          <SearchInput
            type="text"
            placeholder={isArabic ? 'ابحث باسم الدولة أو الكود أو الحالة الافتراضية...' : 'Search by country name, code, or default status...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <ClearButton onClick={() => setSearchTerm('')} title={isArabic ? 'مسح البحث' : 'Clear search'}>
              <FiX size={18} />
            </ClearButton>
          )}
        </SearchContainer>

        {loading ? (
          <EmptyState>{isArabic ? 'جارٍ تحميل الدول...' : 'Loading countries...'}</EmptyState>
        ) : countries.length === 0 ? (
          <TableWrapper>
            <EmptyState>
              <EmptyText>{isArabic ? 'لا توجد دول بعد. أضف أول دولة شحن.' : 'No countries yet. Add your first shipping country.'}</EmptyText>
            </EmptyState>
          </TableWrapper>
        ) : (
          <TableWrapper>
            <Table>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell>{isArabic ? 'الكود' : 'Code'}</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'الاسم' : 'Name'}</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'افتراضي' : 'Default'}</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'تاريخ الإنشاء' : 'Created'}</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'الإجراءات' : 'Actions'}</TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {filteredCountries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan="5" style={{ textAlign: 'center', padding: '40px' }}>
                      <EmptyText>{searchTerm ? (isArabic ? 'لا توجد دول مطابقة للبحث' : 'No countries found matching your search') : (isArabic ? 'لا توجد دول متاحة' : 'No countries available')}</EmptyText>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCountries.map((country) => (
                  <TableRow key={country.id}>
                    <TableCell>
                      <strong>{country.code}</strong>
                    </TableCell>
                    <TableCell>{country.name}</TableCell>
                    <TableCell>
                      <DefaultBadge isDefault={country.is_default}>
                        {country.is_default ? (isArabic ? 'افتراضي' : 'Default') : (isArabic ? 'لا' : 'No')}
                      </DefaultBadge>
                      {!country.is_default && (
                        <button
                          onClick={() => toggleDefault(country.id)}
                          style={{
                            marginLeft: '8px',
                            padding: '4px 8px',
                            fontSize: '11px',
                            border: '1px solid #5efba9',
                            background: 'white',
                            color: '#5efba9',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          {isArabic ? 'تعيين كافتراضي' : 'Set as Default'}
                        </button>
                      )}
                    </TableCell>
                    <TableCell>
                      {country.created_at
                        ? new Date(country.created_at).toLocaleDateString()
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <ActionButtonsCell>
                        <Button variant="secondary" style={{ padding: '6px 12px' }} onClick={() => handleEdit(country)}>
                          <FiEdit />
                        </Button>
                        <Button variant="secondary" style={{ padding: '6px 12px', color: '#dc2626' }} onClick={() => handleDelete(country.id)}>
                          <FiTrash2 />
                        </Button>
                      </ActionButtonsCell>
                    </TableCell>
                  </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableWrapper>
        )}
      </Container>

      {isModalOpen && (
        <ModalOverlay onClick={handleCloseModal}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{editingCountry ? (isArabic ? 'تعديل الدولة' : 'Edit Country') : (isArabic ? 'إضافة دولة جديدة' : 'Add New Country')}</ModalTitle>
              <CloseButton onClick={handleCloseModal}>
                <FiX />
              </CloseButton>
            </ModalHeader>

            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>{isArabic ? 'اختيار سريع للدولة' : 'Quick Select Country'}</Label>
                <select
                  onChange={handleSelectCountry}
                  style={{
                    padding: '10px 16px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: 'white',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">{isArabic ? '-- اختر دولة --' : '-- Select a Country --'}</option>
                  {ALL_COUNTRIES.map(country => (
                    <option key={country.code} value={country.code}>
                      {country.name} ({country.code})
                    </option>
                  ))}
                </select>
                <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px' }}>
                  {isArabic ? 'أو أدخلها يدويًا بالأسفل (إسرائيل مستبعدة من القائمة)' : 'Or enter manually below (Israel is excluded from the list)'}
                </small>
              </FormGroup>

              <FormGroup>
                <Label>{isArabic ? 'رمز الدولة (ISO) *' : 'Country Code (ISO) *'}</Label>
                <Input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  required
                  placeholder="e.g., OM, AE, SA"
                  maxLength={2}
                  style={{ textTransform: 'uppercase' }}
                />
                <small style={{ color: '#6b7280', fontSize: '12px' }}>
                  {isArabic ? 'رمز ISO مكوّن من حرفين' : '2-letter ISO country code'}
                </small>
              </FormGroup>

              <FormGroup>
                <Label>{isArabic ? 'اسم الدولة *' : 'Country Name *'}</Label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., Oman, United Arab Emirates"
                />
              </FormGroup>

              <FormGroup>
                <CheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    checked={formData.is_default}
                    onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                  />
                  <span>{isArabic ? 'تعيين كدولة افتراضية' : 'Set as Default Country'}</span>
                </CheckboxLabel>
                <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px' }}>
                  {isArabic ? 'يمكن تعيين دولة افتراضية واحدة فقط' : 'Only one country can be default at a time'}
                </small>
              </FormGroup>

              <ButtonGroup>
                <CancelButton type="button" onClick={handleCloseModal}>
                  {isArabic ? 'إلغاء' : 'Cancel'}
                </CancelButton>
                <Button type="submit" variant="primary" disabled={submitting}>
                  {submitting ? (isArabic ? 'جارٍ الحفظ...' : 'Saving...') : editingCountry ? (isArabic ? 'تحديث الدولة' : 'Update Country') : (isArabic ? 'إضافة دولة' : 'Add Country')}
                </Button>
              </ButtonGroup>
            </Form>
          </ModalContainer>
        </ModalOverlay>
      )}
    </>
  );
}

